import React from 'react'
import { toast } from 'react-toastify'
import { Box, Grid, MenuItem, Select, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { useMutation } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import UsaAPI from 'api/faber/UsaAPI'
import NormalText from 'common/Typography/NormalText'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

const formikInitialValues = {
  detailCategory: '',
  newValue: ''
}

const NewUserPlanSchema = Yup.object().shape({
  detailCategory: Yup.string().required('Detail category is required'),
  newValue: Yup.string().required('New value is required')
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

interface Props {
  handleClose: () => void
  userId: string
}

export const EditUserDetailsForm = ({ handleClose, userId }: Props) => {
  const faberToken = OrbitSession.get('faberToken')
  const isGlobalUser = userId.includes("|")
  const { mutate: editUserDetails } = useMutation(
    isGlobalUser ? UsaAPI.editUserDetails : OneTimePackApi.editUserDetails,
    {
      onSuccess: () => {
        toast.success('User plan details edit successfully')
        handleClose()
      },
      onError: () => {
        toast.error('Error editing user plan details')
      }
    }
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = (values: EditUserDetailsFormikProps) => {
    const allValues = {
      userId: userId,
      detailCategory: values.detailCategory,
      newValue: values.newValue,
      faberToken: faberToken
    }
    editUserDetails(allValues)
  }

  /**
   * Event handlers
   */
  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(e.target.name, e.target.value)
  }

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewUserPlanSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            <Grid item xs={3}>
              <NormalText isBold>Detail Category</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                defaultValue=''
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='detailCategory'
                label=''
              >
                <MenuItem value='email'>Email</MenuItem>
                {!isGlobalUser && (
                  <>
                    <MenuItem value='firstName'>First Name</MenuItem>
                    <MenuItem value='lastName'>Last Name</MenuItem>
                    <MenuItem value='msisdn'>MSISDN</MenuItem>
                  </>
                )}
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>New Value</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'new-value'
                  }}
                  name='newValue'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.newValue && touched.newValue)}
                  helperText={
                    errors.newValue && touched.newValue
                      ? errors.newValue
                      : null
                  }
                />
              </Box>
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button autoFocus onClick={() => submitForm()}>
                Save changes
              </Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

interface EditUserDetailsFormikProps {
  detailCategory: string
  newValue: string
}
